"use client"

import { deleteComment } from "@/lib/api/comments"
import { Button } from "@/components/ui/button"
import { TrashIcon } from "lucide-react"
import { toast } from "sonner"
import { useState } from "react"

export default function DeleteCommentButton({
  commentId,
  refresh,
}: {
  commentId: string
  /**
   * A function to refresh the comments. Need to pass this down to the delete button for refresh to work
   * after deleting a comment. This is because the useTransition hook (inside usePromiseRouter) used
   * to detect that the refresh has completed will be left in a forever pending state due to useTransition
   * getting interrupted when the DeleteCommentButton component disappears during the refresh (since we
   * deleted the comment).
   */
  refresh: () => void
}) {
  const [isDeleting, setIsDeleting] = useState(false)
  const handleDelete = async () => {
    setIsDeleting(true)

    const promise = deleteComment(commentId).then(() => refresh())

    toast.promise(promise, {
      loading: "Deleting comment...",
      success: "Comment deleted",
      error: "Failed to delete comment",
    })

    await promise

    setIsDeleting(false)
  }

  return (
    <Button
      variant="ghost"
      onClick={handleDelete}
      className="px-2"
      disabled={isDeleting}
    >
      <TrashIcon className="w-4 h-4 text-muted-foreground" />
    </Button>
  )
}
